import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  TextField,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Autocomplete,
} from "@mui/material";
import { colors } from "../../ui/colors";
import { TableSortLabel } from "@mui/material";
import { useInvoices } from "./hooks/useInvoices";
import { IInvoice } from "../create/interfaces/invoice.interfaces";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { InvoiceDetailsView } from "../view/components/DetailsView/index";
import Lottie from "react-lottie";
import { lottieOptions } from "../../jobs2/jobs-calendar-widget/components/TodayJobsPlaceholder";
import CalendarDateSelector from "../../CalendarDateSelector/CalendarDateSelector";
import type { DateRange } from "../../CalendarDateSelector/types";
import { useWatchInvoiceNotifications } from "../../../notifications/hooks/useWatchInvoiceNotifications";
import type { InvoiceListKindParam, InvoiceListPaymentStatusParam } from "./actions/getInvoices";
import { useClients } from "../../clients/hooks/useClients";

const formatCurrency = (amount: number | { value: number } | { total: { value: number } } | null | undefined): string => {
  let value: number;

  if (typeof amount === "number") {
    value = amount;
  } else if (amount && typeof amount === "object") {
    if ("total" in amount && amount.total && typeof amount.total === "object" && "value" in amount.total) {
      value = amount.total.value;
    } else if ("value" in amount) {
      value = amount.value;
    } else {
      return "$0.00";
    }
  } else {
    return "$0.00";
  }

  if (value === null || value === undefined || isNaN(value)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const chipFilterSx = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    height: 30,
    px: 1.5,
    py: 0.25,
    minWidth: "auto",
    borderRadius: "16px !important",
    fontSize: 12,
    fontWeight: 500,
    color: "text.secondary",
    border: "1px solid transparent",
    bgcolor: "transparent",
    transition: "all 120ms ease-in-out",
    "&:hover": {
      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      border: "1px solid var(--fx-surface-border, #E2E8F0)",
    },
    "&.Mui-selected": {
      bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
      border: "1px solid #FDBA74",
      color: "#9A3412",
      fontWeight: 600,
    },
    "&.Mui-selected:hover": {
      bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
      border: "1px solid #FB923C",
    },
  },
  "& .MuiToggleButtonGroup-grouped": {
    border: "none",
    mx: 0.25,
  },
} as const;

interface InvoicesTableProps {
  clientUid?: string;
}

const isDepositInvoiceRow = (invoice: IInvoice): boolean => {
  const n = invoice.billingJobEvents?.length ?? 0;
  return n === 0 && !!invoice.quote;
};

export const InvoicesTable = ({ clientUid }: InvoicesTableProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderByColumn, setOrderByColumn] = useState("createdAt");
  const [selectedInvoice, setSelectedInvoice] = useState<IInvoice | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [invoiceKind, setInvoiceKind] = useState<InvoiceListKindParam>("all");
  const [paymentStatus, setPaymentStatus] = useState<InvoiceListPaymentStatusParam>("all");
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const detailsTriggerRef = useRef<HTMLButtonElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const apiOrderColumn = orderByColumn === "date" ? "createdAt" : orderByColumn;

  const { invoices, getAllInvoices } = useInvoices();
  const { clients, getAllClients, loading: loadingClients } = useClients();

  useEffect(() => {
    if (clientUid) return;
    void getAllClients({ page: 1, pageSize: 200 });
  }, [clientUid]);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      await getAllInvoices({
        page: page + 1,
        pagesize: pageSize,
        order: apiOrderColumn,
        ordertype: order.toUpperCase() as "ASC" | "DESC",
        clientUid: selectedClient?.uid ?? clientUid,
        ...(dateRange.start && dateRange.end ? { createdFrom: dateRange.start, createdTo: dateRange.end } : {}),
        paymentStatus,
        invoiceKind,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    pageSize,
    order,
    apiOrderColumn,
    clientUid,
    selectedClient?.uid,
    dateRange.start,
    dateRange.end,
    paymentStatus,
    invoiceKind,
    getAllInvoices,
  ]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  useWatchInvoiceNotifications({
    dissableSnackbarAlert: true,
    onNotificationArrived: () => {
      void fetchInvoices();
    },
  });

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column: string) => {
    const isAsc = orderByColumn === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderByColumn(column);
  };

  const openDetails = (invoice: IInvoice) => {
    setSelectedInvoice(invoice);
    requestAnimationFrame(() => detailsTriggerRef.current?.click());
  };

  const getPrimaryEvent = (invoice: IInvoice) => {
    const inv = invoice as IInvoice & { jobEvent?: (typeof invoice.billingJobEvents)[0] };
    return inv.jobEvent ?? inv.billingJobEvents?.[0] ?? null;
  };

  const getClientDisplayName = useCallback((client: any): string => {
    if (!client) return "Unknown client";

    const fullName = `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim();
    const alias = String(client.alias ?? "").trim();
    const companyName = String(client.clientCompanyName ?? "").trim();

    if (client.type === "commercial") {
      return companyName || alias || fullName || "Unknown client";
    }

    return alias || fullName || companyName || "Unknown client";
  }, []);

  const getClientName = (invoice: IInvoice): string => {
    const jobEvent = getPrimaryEvent(invoice) as {
      job?: { client?: any };
    } | null;

    const client = jobEvent?.job?.client ?? invoice.quote?.client;

    return getClientDisplayName(client);
  };

  const getJobOrQuoteLabel = (invoice: IInvoice): string => {
    if (isDepositInvoiceRow(invoice)) {
      const quoteId = invoice.quote?.id;

      return quoteId != null ? `Quote #${quoteId} (deposit)` : "Deposit invoice";
    }

    const events = invoice.billingJobEvents ?? (invoice.jobEvent ? [invoice.jobEvent] : []);

    const uniqueJobIds = new Set(events.map((event) => event?.job?.id ?? event?.jobId).filter(Boolean));

    if (uniqueJobIds.size > 1) {
      return `Multiple services (${uniqueJobIds.size})`;
    }

    const primaryEvent = getPrimaryEvent(invoice) as {
      job?: {
        alias?: string;
        type?: string;
      };
    } | null;

    return primaryEvent?.job?.alias || (primaryEvent?.job?.type ? `Service (${primaryEvent.job.type})` : "Service");
  };

  const getJobOrContextSecondary = (invoice: IInvoice): string | null => {
    if (isDepositInvoiceRow(invoice)) {
      return "Quote invoice";
    }

    const events = invoice.billingJobEvents ?? (invoice.jobEvent ? [invoice.jobEvent] : []);

    const uniqueLabels = Array.from(
      new Set(events.map((event) => event?.job?.alias || null).filter((value): value is string => Boolean(value && value.trim()))),
    );

    if (uniqueLabels.length > 1) {
      const preview = uniqueLabels.slice(0, 2).join(" · ");
      const remaining = uniqueLabels.length - 2;

      return remaining > 0 ? `${preview} +${remaining} more` : preview;
    }

    if (uniqueLabels.length === 1) {
      return uniqueLabels[0];
    }

    return null;
  };

  const getPaymentTermsLine = (invoice: IInvoice): string => {
    const q = invoice.quote as
      | {
          payment?: { netDays?: number; label?: string; terms?: string };
          contract?: { paymentTerms?: string; netDays?: number };
          paymentTerms?: string;
        }
      | undefined;
    if (q) {
      const label = q.payment?.label || q.payment?.terms || q.contract?.paymentTerms || q.paymentTerms;
      if (label && String(label).trim()) return String(label).trim();
      const nd = q.payment?.netDays ?? q.contract?.netDays;
      if (typeof nd === "number" && nd > 0) return `Net ${nd}`;
    }
    const notes = invoice.notes?.trim();
    if (notes) {
      const m = notes.match(/net\s*(\d+)/i);
      if (m) return `Net ${m[1]}`;
      if (/end\s*of\s*month|eom\b/i.test(notes)) return "End of month";
    }
    return "—";
  };

  const formatDate = (invoice: IInvoice): string => {
    const raw = invoice.createdAt;
    if (!raw) return "N/A";
    return new Date(raw).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (invoice: IInvoice): string => {
    if (invoice.paidAt && invoice.paidWithPaymentMethod) {
      return colors.success[600];
    }
    return colors.warning[600];
  };

  const getStatusText = (invoice: IInvoice): string => {
    if (invoice.cancelledAt) {
      return "Cancelled";
    }

    if (invoice.paidAt) {
      return "Paid";
    }

    return "Pending";
  };

  const getPaymentMethodText = (invoice: IInvoice): string => {
    if (!invoice.paidAt || !invoice.paidWithPaymentMethod) {
      return "Pending";
    }

    const paymentMethodLabels: Record<string, string> = {
      manual_entry: "Manual",
      card: "Card",
      credit_card: "Credit card",
      debit_card: "Debit card",
      us_bank_account: "ACH",
      ach: "ACH",
      cash: "Cash",
      check: "Check",
    };

    return (
      paymentMethodLabels[invoice.paidWithPaymentMethod] ??
      invoice.paidWithPaymentMethod.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
    );
  };

  const getPaymentMethodColor = (invoice: IInvoice): string => {
    if (!invoice.paidAt || !invoice.paidWithPaymentMethod) {
      return colors.warning[600];
    }

    return colors.success[600];
  };

  const filterKey = useMemo(
    () => `${dateRange.start ?? ""}-${dateRange.end ?? ""}-${invoiceKind}-${paymentStatus}-${clientUid ?? ""}-${selectedClient?.uid ?? ""}`,
    [dateRange.start, dateRange.end, invoiceKind, paymentStatus, clientUid, selectedClient?.uid],
  );

  useEffect(() => {
    setPage(0);
  }, [clientUid]);

  const handleDateFilter = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(0);
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          id: "date",
          label: "Date",
          sortable: true,
          sortKey: "date" as const,
          width: 100,
          align: "center" as const,
        },
        {
          id: "identifierId",
          label: "Identifier",
          sortable: false,
          width: 130,
          align: "center" as const,
        },
        {
          id: "client",
          label: "Client",
          sortable: false,
          width: 110,
          align: "left" as const,
        },
        {
          id: "job",
          label: "Job / context",
          sortable: false,
          width: 130,
          align: "left" as const,
        },
        {
          id: "type",
          label: "Type",
          sortable: false,
          width: 100,
          align: "center" as const,
        },
        {
          id: "totalCost",
          label: "Amount",
          sortable: true,
          sortKey: "totalCost" as const,
          width: 100,
          align: "center" as const,
        },
        {
          id: "status",
          label: "Status",
          sortable: false,
          width: 100,
          align: "center" as const,
        },
        {
          id: "method",
          label: "Method",
          sortable: false,
          width: 120,
          align: "center" as const,
        },
      ] as const,
    [],
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        p: { xs: 1.5, sm: 2 },
        gap: 2,
      }}
    >
      <Stack direction="column" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em", mb: 1.5 }}>Invoices</Typography>

          <Stack spacing={1.25}>
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              {!clientUid ? (
                <Autocomplete
                  size="small"
                  disablePortal={false}
                  options={clients?.items ?? []}
                  value={selectedClient}
                  loading={loadingClients}
                  onChange={(_, value) => {
                    setSelectedClient(value);
                    setPage(0);
                  }}
                  getOptionLabel={getClientDisplayName}
                  isOptionEqualToValue={(option: any, value: any) => option.uid === value.uid}
                  filterOptions={(options, state) => {
                    const term = state.inputValue.trim().toLowerCase();

                    if (!term) return options;

                    return options.filter((client: any) => {
                      const values = [
                        client.clientCompanyName,
                        client.alias,
                        client.firstName,
                        client.lastName,
                        `${client.firstName ?? ""} ${client.lastName ?? ""}`,
                        client.email,
                      ];

                      return values.some((value) =>
                        String(value ?? "")
                          .toLowerCase()
                          .includes(term),
                      );
                    });
                  }}
                  sx={{
                    minWidth: 280,
                    maxWidth: 360,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      borderRadius: "16px",
                      minHeight: 36,
                      "& fieldset": {
                        borderColor: "var(--fx-surface-border, #E2E8F0)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.orange[400],
                      },
                    },
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        p: 0.75,
                        width: 260,
                        borderRadius: 2,
                        zIndex: 20000,
                      },
                    },
                    popper: {
                      sx: {
                        zIndex: (muiTheme) => muiTheme.zIndex.modal + 50,
                      },
                    },
                  }}
                  renderInput={(params) => <TextField {...params} size="small" placeholder="Search by client or company..." />}
                  renderOption={(props, option) => {
                    const fullName = `${option.firstName ?? ""} ${option.lastName ?? ""}`.trim();
                    const primaryName = getClientDisplayName(option);

                    const secondaryName =
                      option.type === "commercial" && fullName && fullName !== primaryName
                        ? fullName
                        : option.type !== "commercial" && option.clientCompanyName && option.clientCompanyName !== primaryName
                          ? option.clientCompanyName
                          : null;

                    return (
                      <Box component="li" {...props} key={option.uid} sx={{ py: 0.75 }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {primaryName}
                          </Typography>

                          {secondaryName ? (
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: "text.secondary",
                              }}
                            >
                              {secondaryName}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  }}
                  noOptionsText="No clients match your search"
                />
              ) : null}

              <ToggleButtonGroup
                exclusive
                size="small"
                value={invoiceKind}
                onChange={(_, v) => {
                  if (v != null) {
                    setInvoiceKind(v);
                    setPage(0);
                  }
                }}
                sx={{
                  flexWrap: "wrap",
                  ...chipFilterSx,
                }}
              >
                <ToggleButton value="all">All types</ToggleButton>
                <ToggleButton value="deposit">Deposits</ToggleButton>
                <ToggleButton value="standard">Non-deposit</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={paymentStatus}
                onChange={(_, v) => {
                  if (v != null) {
                    setPaymentStatus(v);
                    setPage(0);
                  }
                }}
                sx={{
                  flexWrap: "wrap",
                  ...chipFilterSx,
                }}
              >
                <ToggleButton value="all">All status</ToggleButton>
                <ToggleButton value="paid">Paid</ToggleButton>
                <ToggleButton value="pending">Pending</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "flex-start" }} sx={{ flex: 1, minHeight: 0 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            bgcolor: theme.palette.grey[50],
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 320, maxHeight: { xs: 420, md: 460 }, overflowY: "auto" }}>
            {isLoading && !invoices.items.length ? (
              <Box sx={{ height: 360, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={28} thickness={5} sx={{ color: colors.orange[400] }} />
              </Box>
            ) : (
              <Table stickyHeader size="small" key={filterKey} sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sx={{
                          width: col.width,
                          minWidth: col.width,
                          maxWidth: col.width,
                          bgcolor: theme.palette.common.white,
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "text.secondary",
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {"sortKey" in col ? (
                          <TableSortLabel
                            active={orderByColumn === col.sortKey}
                            direction={orderByColumn === col.sortKey ? order : "asc"}
                            onClick={() => handleSort(col.sortKey)}
                            sx={{
                              "&.MuiTableSortLabel-active": {
                                color: colors.orange[500],
                              },
                              "& .MuiTableSortLabel-icon": {
                                ml: 0.5,
                                color: `${colors.orange[500]} !important`,
                              },
                            }}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {invoices.items?.length > 0 ? (
                    invoices.items.map((invoice) => {
                      const statusColor = getStatusColor(invoice);
                      const deposit = isDepositInvoiceRow(invoice);

                      return (
                        <TableRow
                          key={invoice.uid}
                          onClick={() => openDetails(invoice)}
                          sx={{
                            cursor: "pointer",
                            bgcolor: theme.palette.common.white,
                            "&:hover": {
                              bgcolor: theme.palette.grey[50],
                            },
                            transition: "background-color 0.15s ease",
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              width: 100,
                              minWidth: 100,
                              maxWidth: 100,
                              py: 0.75,
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {formatDate(invoice)}
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              width: 130,
                              minWidth: 130,
                              maxWidth: 130,
                              py: 0.75,
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              color: "text.primary",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={invoice.identifierId || ""}
                          >
                            {invoice.identifierId || "—"}
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: 110,
                              minWidth: 110,
                              maxWidth: 110,
                              py: 0.75,
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              color: "text.primary",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={getClientName(invoice)}
                          >
                            {getClientName(invoice)}
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: 130,
                              minWidth: 130,
                              maxWidth: 130,
                              py: 0.75,
                              fontSize: "0.8125rem",
                              color: "text.secondary",
                              overflow: "hidden",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.8125rem",
                                lineHeight: 1.25,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {getJobOrQuoteLabel(invoice)}
                            </Typography>

                            {getJobOrContextSecondary(invoice) ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.1,
                                  color: "text.secondary",
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {getJobOrContextSecondary(invoice)}
                              </Typography>
                            ) : !isMobile ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.1,
                                  color: "text.secondary",
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                Payment terms: {getPaymentTermsLine(invoice)}
                              </Typography>
                            ) : null}
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              width: 100,
                              minWidth: 100,
                              maxWidth: 100,
                              py: 0.75,
                            }}
                          >
                            <Chip
                              label={deposit ? "Deposit" : "Standard"}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: deposit ? `${theme.palette.info.main}12` : "transparent",
                                color: deposit ? theme.palette.info.dark : "text.secondary",
                                borderColor: deposit ? `${theme.palette.info.main}33` : "divider",
                              }}
                            />
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              width: 100,
                              minWidth: 100,
                              maxWidth: 100,
                              py: 0.75,
                              fontWeight: 700,
                              fontSize: "0.8125rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {formatCurrency(invoice.totalCost)}
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              width: 100,
                              minWidth: 100,
                              maxWidth: 100,
                              py: 0.75,
                            }}
                          >
                            <Chip
                              label={getStatusText(invoice)}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: `${statusColor}14`,
                                color: statusColor,
                                border: "none",
                              }}
                            />
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              width: 120,
                              minWidth: 120,
                              maxWidth: 120,
                              py: 0.75,
                            }}
                          >
                            <Chip
                              label={getPaymentMethodText(invoice)}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: `${getPaymentMethodColor(invoice)}14`,
                                color: getPaymentMethodColor(invoice),
                                border: "none",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{
                          border: "none",
                          py: 10,
                          verticalAlign: "middle",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Lottie options={lottieOptions} height={120} width={120} isClickToPauseDisabled />

                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              mt: 1,
                            }}
                          >
                            {selectedClient ? "No invoices match this client" : "No invoices in this range"}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <TablePagination
            component="div"
            rowsPerPageOptions={[5]}
            count={invoices.totalItems ?? 0}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              "& .MuiTablePagination-toolbar": { minHeight: 48 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8125rem" },
            }}
          />
        </Paper>

        <Box sx={{ flexShrink: 0, width: { xs: "100%", lg: 280 } }}>
          <CalendarDateSelector
            onFilter={handleDateFilter}
            modes={["day", "week", "month"]}
            defaultMode="week"
            allowFutureDates={false}
            width="100%"
          />
        </Box>
      </Stack>

      <BaseModalComponent
        modalLabel={selectedInvoice ? `Invoice — ${(selectedInvoice.identifierId || selectedInvoice.uid).slice(0, 12)}` : "Invoice"}
        modalKey={selectedInvoice ? `Dashboard-Invoice-Details-${selectedInvoice.uid}` : "Dashboard-Invoice-Details"}
        elementTrigger={<button ref={detailsTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
        preferModalContent
      >
        {selectedInvoice && <InvoiceDetailsView invoiceUid={selectedInvoice.uid} scopeOfWorkUid={selectedInvoice.scopeOfWorkUid} />}
      </BaseModalComponent>
    </Box>
  );
};
